import * as queryString from 'query-string';

export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_UNPROCESSABLE_ENTITY = 422;

export const importedEventFetchStatus = {
    SUCCESS: 'success',
    NOT_AVAILABLE: 'not-available',
    VALIDATION_FAILURE: 'validation-failure',
    GENERIC_FAILURE: 'generic-failure',
};

const mapImportedEventFetchError = (error) => {
    const httpStatus = error?.response?.status;

    if (httpStatus === HTTP_STATUS_NOT_FOUND) {
        return { status: importedEventFetchStatus.NOT_AVAILABLE };
    }

    if (httpStatus === HTTP_STATUS_UNPROCESSABLE_ENTITY) {
        return { status: importedEventFetchStatus.VALIDATION_FAILURE };
    }

    return {
        status: importedEventFetchStatus.GENERIC_FAILURE,
        showReloadButton: true,
    };
};

export const getImportedEventSummary = async (axiosInstance, eventId) => {
    try {
        const response = await axiosInstance.get(`v1/imported/events/${eventId}`);

        // A 2xx with no usable body would otherwise reach the page as "loaded, but empty" and
        // render nothing at all. Treat it as a load failure so the page always has a state.
        if (!response?.data?.id) {
            return {
                status: importedEventFetchStatus.GENERIC_FAILURE,
                showReloadButton: true,
            };
        }

        return {
            status: importedEventFetchStatus.SUCCESS,
            data: response.data,
        };
    } catch (error) {
        if (error?.response?.status === HTTP_STATUS_NOT_FOUND) {
            return {
                status: importedEventFetchStatus.NOT_AVAILABLE,
            };
        }
        return {
            status: importedEventFetchStatus.GENERIC_FAILURE,
            showReloadButton: true,
        };
    }
};

export const getImportedEventPlayers = async (axiosInstance, eventId, queryParams) => {
    try {
        const response = await axiosInstance.get(`v1/imported/events/${eventId}/players`, {
            params: queryParams,
            paramsSerializer: (params) => queryString.stringify(params),
        });

        // Same reasoning as getImportedEventSummary: a 2xx whose body is not the expected envelope
        // would otherwise write undefined into the reducer and present as a loaded, empty tab.
        if (!Array.isArray(response?.data?.players)) {
            return {
                status: importedEventFetchStatus.GENERIC_FAILURE,
                showReloadButton: true,
            };
        }

        return {
            status: importedEventFetchStatus.SUCCESS,
            data: response.data,
        };
    } catch (error) {
        return mapImportedEventFetchError(error);
    }
};

export const getImportedEventDraws = async (axiosInstance, eventId) => {
    try {
        // The bare array is intentional here: unlike /players and /filters, this endpoint does
        // not wrap its payload in an envelope. Reading response.data.draws silently yields
        // undefined instead of a thrown error.
        const response = await axiosInstance.get(`v1/imported/events/${eventId}/draws`);

        if (!Array.isArray(response?.data)) {
            return {
                status: importedEventFetchStatus.GENERIC_FAILURE,
                showReloadButton: true,
            };
        }

        return {
            status: importedEventFetchStatus.SUCCESS,
            data: response.data,
        };
    } catch (error) {
        return mapImportedEventFetchError(error);
    }
};

export const getImportedEventResults = async (axiosInstance, eventId, queryParams) => {
    try {
        const response = await axiosInstance.get(`v1/imported/events/${eventId}/results`, {
            params: queryParams,
            paramsSerializer: (params) => queryString.stringify(params),
        });

        // Envelope guard, not the bare-array guard /draws uses: this endpoint wraps its matches
        // in { draws, totalCount }, so Array.isArray(response.data) would reject every real
        // response and Array.isArray(response.data.draws) is the actual shape to check.
        if (!Array.isArray(response?.data?.draws)) {
            return {
                status: importedEventFetchStatus.GENERIC_FAILURE,
                showReloadButton: true,
            };
        }

        return {
            status: importedEventFetchStatus.SUCCESS,
            data: response.data,
        };
    } catch (error) {
        return mapImportedEventFetchError(error);
    }
};

const isFilterEnvelope = (data) => !!data && typeof data === 'object' && !Array.isArray(data);

export const getImportedEventFilters = async (axiosInstance, eventId) => {
    try {
        const response = await axiosInstance.get(`v1/imported/events/${eventId}/filters`);

        // Deliberately weaker than the players guard: which filter keys the endpoint returns
        // varies by event, so only the envelope itself is asserted. A null or array body would
        // otherwise present as a loaded filter bar with every dropdown silently empty.
        if (!isFilterEnvelope(response?.data)) {
            return {
                status: importedEventFetchStatus.GENERIC_FAILURE,
                showReloadButton: true,
            };
        }

        return {
            status: importedEventFetchStatus.SUCCESS,
            data: response.data,
        };
    } catch (error) {
        return mapImportedEventFetchError(error);
    }
};
